import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  ComponentMultiStyleConfig,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import {
  CommentThreadInputContent,
  MainCommentBlockContent,
  ReplyCommentBlockItemContent,
} from "../types";
import { CMMainCommentBlock } from "../CMMainCommentBlock/CMMainCommentBlock";
import { CMReplyCommentBlock } from "../CMReplyCommentBlock/CMReplyCommentBlock";
import { CMCommentThreadInput } from "../CMCommentThreadInput/CMCommentThreadInput";

export type CMCommentThreadProps = {
  /**
   * コメントスレッドID
   */
  commentThreadId: string;

  /**
   * スレッドタイトルエリアの情報
   */
  mainContent: MainCommentBlockContent;

  /**
   * 返信エリアの情報
   */
  replyContents: ReplyCommentBlockItemContent[];

  /**
   * 返信入力エリアの情報
   */
  commentInputContent: CommentThreadInputContent;

  /**
   * 未読フラグ
   * trueの場合: 未読
   */
  isUnread?: boolean;

  /**
   * 開閉ボタンイベント
   * @param commentId
   * @param isOpen 開閉状態
   * 開いている状態:true
   */
  onOpenArea?: (commentId: string, isOpen: boolean) => void;

  /**
   * 既読更新イベント
   * @param commentId
   */
  onRead?: (commentId: string) => void;

  /**
   * ファイルが追加されたときに、その追加ファイルを引数として呼び出される。
   */
  onAddFile: (file: File) => Promise<void>;

  /**
   * ファイルが削除されたときに、その削除ファイルを引数として呼び出される。
   */
  onDeleteFile: (fileId: string) => void;

  /**
   * ファイルが選択されたときに、そのファイルを引数として呼び出される。
   * @param fileId
   * @param fileName ファイル名
   */
  onDownloadFile?: (
    commentThreadId: string,
    fileId: string,
    fileName: string,
  ) => void;

  /**
   * コメント変更時のイベント
   * @param comment コメント
   */
  onChangeComment?: (comment: string) => void;

  /**
   * 送信ボタンイベント
   * @param commentThreadId コメントスレッドID
   * @param inputText 入力コメント
   * @param uploadFiles 添付ファイル
   */
  onSubmit: () => void;
} & ComponentStyleProps;

/**
 * 質疑応答コンポーネント
 * メインコメントに対して、複数の返信が行える
 */
export const CMCommentThread = ({
  commentThreadId,
  mainContent,
  replyContents,
  commentInputContent,
  isUnread = true,

  onOpenArea,
  onRead,
  onSubmit,
  onAddFile,
  onDownloadFile,
  onDeleteFile,
  onChangeComment,

  sx,
  ...rest
}: CMCommentThreadProps) => {
  const styles = useMultiStyleConfig("CMCommentThreadStyles");

  /**
   * 開閉状態
   * trueの場合: 開いている状態
   */
  const [isOpen, setStatus] = useState<boolean>(false);

  /**
   * コメントエリアの開閉スタイル
   */
  const appliedReplyAreaStyles = useMemo(
    () => ({
      ...styles.replyAreaBase,
      ...(isOpen ? styles.replyAreaExpand : styles.replyAreaShrink),
    }),
    [isOpen, styles],
  );

  /**
   * 開閉イベントの通知
   */
  const handleToggle = useCallback(
    (isOpen: boolean) => {
      setStatus(isOpen);
      onOpenArea?.(commentThreadId, isOpen);
    },
    [commentThreadId, onOpenArea],
  );

  /**
   * 既読更新イベントの通知
   */
  const handleRead = useCallback(() => {
    onRead?.(commentThreadId);
  }, [commentThreadId, onRead]);

  return (
    <Box sx={{ ...styles.container, ...sx }} {...rest}>
      {/*メインコメントエリア*/}
      <CMMainCommentBlock
        commentThreadId={commentThreadId}
        content={mainContent}
        isUnread={isUnread}
        onToggle={(_commentId, isOpen) => {
          handleToggle(isOpen);
        }}
        onRead={handleRead}
        onDownloadFile={(fileId: string, fileName: string) => {
          onDownloadFile?.(commentThreadId, fileId, fileName);
        }}
        sx={styles.mainCommentArea}
      />

      {/*返信エリア*/}
      <Box sx={appliedReplyAreaStyles}>
        {/*返信コメントエリア*/}
        <Box sx={styles.replyCommentArea}>
          <CMReplyCommentBlock
            contents={replyContents}
            onDownloadFile={(fileId: string, fileName: string) => {
              onDownloadFile?.(commentThreadId, fileId, fileName);
            }}
            sx={styles.replyComment}
          />
        </Box>

        {/*返信コメント入力エリア*/}
        <CMCommentThreadInput
          placeholder={commentInputContent.placeholder}
          comment={commentInputContent.comment}
          files={commentInputContent.files}
          buttonUploadLabel={commentInputContent.buttonUploadLabel}
          uploadingLabel={commentInputContent.uploadingLabel}
          buttonSubmitLabel={commentInputContent.buttonSubmitLabel}
          onAddFile={onAddFile}
          onDeleteFile={onDeleteFile}
          onSubmit={onSubmit}
          onDownloadFile={(fileId: string, fileName: string) => {
            onDownloadFile?.(commentThreadId, fileId, fileName);
          }}
          onChangeComment={onChangeComment}
          sx={styles.replyCommentInput}
        />
      </Box>
    </Box>
  );
};

export const CMCommentThreadStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "mainCommentArea",
    "replyArea",
    "replyCommentArea",
    "replyComment",
    "replyCommentInput",
  ],
  baseStyle: {
    container: {
      display: "flex",
      flexDirection: "column",
      paddingLeft: "12px",
      paddingRight: "12px",
      paddingTop: "10px",
      paddingBottom: "16px",
      border: "solid 1px",
      borderColor: "gray.300",
      borderRadius: "lg",
    },

    mainCommentArea: {
      position: "relative",
    },
    replyAreaBase: {
      position: "relative",
    },
    replyAreaShrink: {
      h: "0px",
      overflow: "hidden",
    },
    replyAreaExpand: {},

    replyCommentArea: {
      borderWidth: "1px",
      borderLeftStyle: "none",
      borderRightStyle: "none",
      borderColor: "transparent",
      borderBottomColor: "gray.300",
    },
    replyComment: {
      position: "relative",
      marginTop: "8px",
      paddingLeft: "36px",
    },

    replyCommentInput: {
      position: "relative",
      marginTop: "16px",
    },
  },
};
